import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LayoutGrid, Grid3x3, Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({});
  const [compact, setCompact] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");

  useEffect(() => {
    (async () => {
      const [p, s] = await Promise.all([
        api.get("/products"),
        api.get("/settings"),
      ]);
      setProducts((p.data || []).filter((x) => x.active !== false));
      setSettings(s.data || {});
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const inCat = cat === "ALL" || p.category === cat;
      const inQ =
        !q ||
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        (p.category || "").toLowerCase().includes(q.toLowerCase());
      return inCat && inQ;
    });
  }, [products, cat, q]);

  return (
    <div data-testid="catalog-page" className="bg-ivory min-h-screen">
      {/* Header band */}
      <section className="relative pt-32 md:pt-44 pb-14 md:pb-20 border-b border-navy/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <p className="chapter-num mb-5">— The catalogue</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-navy"
          >
            Every fabric,
            <br />
            <span className="italic text-gold">one house.</span>
          </motion.h1>
          <p className="mt-6 max-w-xl text-navy/60 text-base md:text-lg font-light">
            Browse the full B'Premium collection. Tap a fabric to enquire on
            WhatsApp or download its PDF catalog.
          </p>
        </div>
      </section>

      {/* Sticky controls */}
      <section className="sticky top-16 md:top-20 z-30 bg-ivory/85 backdrop-blur-xl border-b border-navy/10">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          {/* search */}
          <div className="relative flex-1 max-w-md">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40"
            />
            <Input
              data-testid="catalog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search fabrics…"
              className="pl-9 bg-white/60 border-navy/15 text-navy placeholder:text-navy/40 h-10 rounded-full"
            />
          </div>

          {/* categories */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-5 md:mx-0 px-5 md:px-0">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-filter-${c}`}
                onClick={() => setCat(c)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                  cat === c
                    ? "bg-navy border-navy text-ivory"
                    : "border-navy/20 text-navy/70 hover:border-navy/50 hover:text-navy"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* view toggle */}
          <div className="flex items-center gap-1 rounded-full border border-navy/20 p-1 self-start md:self-auto">
            <button
              data-testid="view-default"
              onClick={() => setCompact(false)}
              className={`px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase flex items-center gap-1.5 transition-colors ${
                !compact ? "bg-navy text-ivory" : "text-navy/70"
              }`}
              aria-label="Default grid"
            >
              <LayoutGrid size={12} /> Default
            </button>
            <button
              data-testid="view-compact"
              onClick={() => setCompact(true)}
              className={`px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase flex items-center gap-1.5 transition-colors ${
                compact ? "bg-navy text-ivory" : "text-navy/70"
              }`}
              aria-label="Compact grid"
            >
              <Grid3x3 size={12} /> Compact
            </button>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 md:py-16">
        {filtered.length === 0 ? (
          <div
            data-testid="catalog-empty"
            className="border border-dashed border-navy/20 py-24 text-center text-navy/50"
          >
            No fabrics match your filters.
          </div>
        ) : (
          <div
            data-testid="catalog-grid"
            className={
              compact
                ? "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3"
                : "grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6"
            }
          >
            {filtered.map((p, i) => (
              <ProductCard
                key={p.id}
                product={p}
                index={i}
                compact={compact}
                whatsappNumber={settings.whatsapp}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
